import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import "dayjs/locale/de";
import * as Yup from "yup";

const DateField = forwardRef(
	(
		props: {
			value: string;
			setValue: (value: string) => void;
			label: string;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
			isLoaded?: boolean;
			readOnly?: boolean;
		},
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t, i18n } = useTranslation();

		const [item, setItem] = useState<{ date: string }>({
			date: "",
		});

		const [open, setOpen] = useState<boolean>(false);

		const [isLoaded, setIsLoaded] = useState<boolean>(false);

		const validationSchema = Yup.object().shape({
			date: Yup.string()
				.matches(/^\d{4}-\d{2}-\d{2}$/, t("date-is-invalid", { ns: "validation" }))
				.required(t("date-is-required", { ns: "validation" })),
		});

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(item, { abortEarly: false });
				props.setErrors?.({});
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
					props.setErrors?.({ ...localErrors });
				}

				return localErrors;
			}
		}, [item, props, validationSchema]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		useEffect(() => {
			if (props.value && !isLoaded && props.isLoaded) {
				setIsLoaded(true);
				setItem({ ...item, date: dayjs(props.value).format("YYYY-MM-DD") });
			}
		}, [props.value, props.isLoaded]);

		useEffect(() => {
			if (item.date) props.setValue(dayjs(item.date).format("YYYY-MM-DD"));
		}, [item.date]);

		const lang = i18n.language ?? "en";

		const format = lang === "de" ? "DD.MM.YYYY" : "MM/DD/YYYY";

		return (
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang}>
				<DatePicker
					open={open}
					onClose={props.readOnly ? undefined : () => setOpen(false)}
					onOpen={props.readOnly ? undefined : () => setOpen(true)}
					format={format}
					sx={{ width: "50%", fontColor: "black" }}
					label={props.label}
					value={dayjs(item.date, "YYYY-MM-DD")}
					onChange={(newValue) => {
						const parsedDate = newValue ? dayjs(newValue, format).format("YYYY-MM-DD") : null;
						setItem({ ...item, date: parsedDate ?? "" });
						props.setErrors?.({ ...props.errors, date: "" });
					}}
					slotProps={{
						textField: {
							helperText: props.errors?.date as string,
							error: !!props.errors?.date,
							onClick: () => setOpen(!open),
						},
					}}
					readOnly={props.readOnly}
				/>
			</LocalizationProvider>
		);
	},
);

DateField.displayName = "DateField";
export default DateField;
